import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import "@/lib/pdfWorker";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, MousePointerClick, Trash2, Save, Lock } from "lucide-react";

/**
 * Two-in-one page:
 *  - Uploader mode: click on any PDF page to drop signature slots. Drag not needed for MVP;
 *    delete + re-place is fine. Save persists all slots.
 *  - Sign / view mode: any assigned user sees the same PDF with slots as green boxes.
 *    Clicking an unsigned slot opens the sign panel. Signed slots show the signature preview.
 */
export default function PolicyEditor() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [pageWidth, setPageWidth] = useState(720);
  const [slots, setSlots] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [signing, setSigning] = useState(null); // slot object
  const [signerName, setSignerName] = useState("");
  const [busy, setBusy] = useState(false);

  const isUploader = doc && (user?.role === "super_admin" || user?.id === doc.created_by);
  const canEditSlots = isUploader && (doc?.signatures?.length ?? 0) === 0;

  const load = async () => {
    try {
      const { data } = await api.get(`/policies/${id}`);
      setDoc(data);
      setSlots(data.slots || []);
      setSignerName(user?.name || "");
      setDirty(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const pdfSrc = useMemo(() => (doc ? `data:application/pdf;base64,${doc.pdf_base64}` : null), [doc]);

  const onClickPage = (pageNumber, e) => {
    if (!canEditSlots) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x_pct = ((e.clientX - rect.left) / rect.width) * 100;
    const y_pct = ((e.clientY - rect.top) / rect.height) * 100;
    const label = prompt("Label for this signature slot (e.g. 'Employee' or 'Line Manager'):", `Signature ${slots.length + 1}`);
    if (label === null) return;
    setSlots((s) => [...s, {
      id: `slot-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      page: pageNumber,
      x_pct: Math.max(0, Math.min(100, x_pct - 8)), // centre-ish default 16x8 box
      y_pct: Math.max(0, Math.min(100, y_pct - 4)),
      w_pct: 16,
      h_pct: 8,
      label: label.trim() || `Signature ${slots.length + 1}`,
    }]);
    setDirty(true);
  };

  const removeSlot = (slotId) => {
    if (!canEditSlots) return;
    if (!window.confirm("Remove this signature slot?")) return;
    setSlots((s) => s.filter((x) => x.id !== slotId));
    setDirty(true);
  };

  const updateSlot = (slotId, patch) => {
    setSlots((s) => s.map((x) => (x.id === slotId ? { ...x, ...patch } : x)));
    setDirty(true);
  };

  const duplicateSlot = (slotId) => {
    setSlots((prev) => {
      const src = prev.find((x) => x.id === slotId);
      if (!src) return prev;
      const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
      // Offset the copy a bit so it doesn't sit exactly on top of the original.
      const copy = {
        ...src,
        id: `slot-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        x_pct: clamp(src.x_pct + 3, 0, 100 - src.w_pct),
        y_pct: clamp(src.y_pct + 3, 0, 100 - src.h_pct),
      };
      return [...prev, copy];
    });
    setDirty(true);
  };

  const saveSlots = async () => {
    setBusy(true);
    try {
      await api.put(`/policies/${id}/slots`, { slots });
      toast.success(`${slots.length} signature slot${slots.length === 1 ? "" : "s"} saved`);
      setDirty(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const submitSign = async () => {
    if (!signing) return;
    if (!signerName.trim()) { toast.error("Enter your full name"); return; }
    setBusy(true);
    try {
      // Simple typed signature — captures the typed name as SVG-ish text data URL for consistency.
      const canvas = document.createElement("canvas");
      canvas.width = 400; canvas.height = 120;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "#111827";
      ctx.font = "italic 42px 'Cormorant Garamond', 'Times New Roman', serif";
      ctx.textBaseline = "middle";
      ctx.fillText(signerName.trim(), 12, canvas.height / 2);
      const signature_data = canvas.toDataURL("image/png");
      await api.post(`/policies/${id}/sign`, {
        slot_id: signing.id,
        signature_data,
        signer_name: signerName.trim(),
      });
      toast.success(`Signed as ${signerName}`);
      setSigning(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (!doc) return <div className="text-sm text-zinc-500">Loading…</div>;

  const signatureByslot = Object.fromEntries((doc.signatures || []).map((s) => [s.slot_id, s]));
  const mySlots = (doc.signatures || []).filter((s) => s.user_id === user?.id);
  const openSlots = (slots || []).filter((s) => !signatureByslot[s.id]);
  const alreadySignedByMe = mySlots.length > 0;

  return (
    <div>
      <button onClick={() => nav(-1)} className="text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> Back
      </button>
      <div className="flex items-start justify-between mb-6 gap-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Policy document</div>
          <h1 className="text-3xl font-bold text-zinc-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{doc.name}</h1>
          <div className="text-xs text-zinc-500 mt-1">
            Uploaded by <b>{doc.created_by_name}</b> on {new Date(doc.created_at).toLocaleString()} · {slots.length} signature slot{slots.length === 1 ? "" : "s"} · {(doc.signatures || []).length} signed
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEditSlots && dirty && (
            <button
              onClick={saveSlots}
              disabled={busy}
              style={{ background: "var(--brand-primary)" }}
              data-testid="policy-save-slots-btn"
              className="text-white px-4 py-2 rounded-sm text-sm font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" /> Save slots
            </button>
          )}
          {!canEditSlots && isUploader && (doc.signatures?.length ?? 0) > 0 && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-sm flex items-center gap-1">
              <Lock className="w-3.5 h-3.5" /> Slots locked — signatures captured
            </div>
          )}
        </div>
      </div>

      {canEditSlots && (
        <div className="mb-4 text-xs text-zinc-700 bg-blue-50 border border-blue-200 px-4 py-2 rounded-sm flex items-center gap-2">
          <MousePointerClick className="w-4 h-4 text-blue-700" />
          <b>Click a page</b> to drop a slot. <b>Drag the body</b> to move · <b>drag any edge or corner</b> to resize · toolbar on top: <b>⧉</b> duplicate · <b>×</b> delete. Slots lock once anyone signs.
        </div>
      )}

      {/* PDF pages */}
      <div className="bg-zinc-100 border border-zinc-200 p-4 rounded-sm">
        <Document
          file={pdfSrc}
          onLoadSuccess={({ numPages: n }) => setNumPages(n)}
          onLoadError={(err) => toast.error(`PDF failed to load: ${err.message}`)}
          loading={<div className="text-sm text-zinc-500 py-10 text-center">Loading PDF…</div>}
        >
          {Array.from({ length: numPages }).map((_, i) => {
            const pageNumber = i + 1;
            const pageSlots = slots.filter((s) => s.page === pageNumber);
            return (
              <div key={pageNumber} className="mb-4 flex justify-center">
                <div
                  className="relative bg-white shadow-sm"
                  style={{ width: pageWidth, cursor: canEditSlots ? "crosshair" : "default" }}
                  onClick={canEditSlots ? (e) => {
                    if ((e.target).closest?.("[data-slot-id]")) return;
                    onClickPage(pageNumber, e);
                  } : undefined}
                  data-testid={`policy-page-${pageNumber}`}
                >
                  <Page pageNumber={pageNumber} width={pageWidth} renderTextLayer={false} renderAnnotationLayer={false} />
                  {pageSlots.map((s) => {
                    const sig = signatureByslot[s.id];
                    return (
                      <SlotOverlay
                        key={s.id}
                        slot={s}
                        sig={sig}
                        editable={canEditSlots}
                        alreadySignedByMe={alreadySignedByMe}
                        onSign={() => setSigning(s)}
                        onDelete={() => removeSlot(s.id)}
                        onUpdate={(next) => updateSlot(s.id, next)}
                        onDuplicate={() => duplicateSlot(s.id)}
                      />
                    );
                  })}
                  <div className="absolute bottom-1 right-2 text-[10px] text-zinc-400">Page {pageNumber} / {numPages}</div>
                </div>
              </div>
            );
          })}
        </Document>
      </div>

      {/* Sign panel */}
      {signing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setSigning(null)}>
          <div className="bg-white border border-zinc-200 rounded-sm max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-zinc-200 text-sm font-semibold">Sign as: {signing.label}</div>
            <div className="p-5 space-y-4">
              <div className="text-sm text-zinc-700">
                By signing, you agree to this policy. The system will stamp your name, current date/time and IP address alongside your signature.
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Type your full name to sign</label>
                <input
                  data-testid="policy-signer-name-input"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm"
                  autoFocus
                />
                <div className="mt-2 border border-zinc-200 bg-zinc-50 p-3 rounded-sm text-center italic text-2xl" style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}>
                  {signerName || "—"}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setSigning(null)} className="px-4 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50">Cancel</button>
                <button
                  data-testid="policy-sign-submit-btn"
                  disabled={busy}
                  onClick={submitSign}
                  style={{ background: "var(--brand-primary)" }}
                  className="px-4 py-2 text-sm text-white rounded-sm hover:opacity-90 disabled:opacity-50"
                >
                  {busy ? "Signing…" : "Sign policy"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!canEditSlots && !alreadySignedByMe && openSlots.length > 0 && (
        <div className="mt-4 text-sm text-zinc-700 bg-blue-50 border border-blue-200 px-4 py-3 rounded-sm">
          Click any blue box above to sign this policy. {openSlots.length} slot{openSlots.length === 1 ? "" : "s"} remaining.
        </div>
      )}
      {alreadySignedByMe && (
        <div className="mt-4 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 px-4 py-3 rounded-sm">
          You&apos;ve signed this policy. Your signature is stamped above.
        </div>
      )}
    </div>
  );
}

/**
 * Interactive signature slot overlay.
 *   - Uploader (editable=true, no sig): drag body to move; drag SE corner to resize; × to delete.
 *   - Signer (editable=false, no sig): click anywhere on the slot to open the sign modal.
 *   - Signed slot: locked, shows the stamped signature + name/date/IP.
 * Coords stay as % of the page container so the layout is resolution-independent.
 */
function SlotOverlay({ slot: s, sig, editable, alreadySignedByMe, onSign, onDelete, onUpdate, onDuplicate }) {
  const [drag, setDrag] = React.useState(null); // { mode, dir, startX, startY, orig }
  const boxRef = React.useRef(null);

  React.useEffect(() => {
    if (!drag) return;
    const parent = boxRef.current?.parentElement;
    const rect = parent?.getBoundingClientRect();
    if (!rect) return;

    const onMove = (e) => {
      const dxPct = ((e.clientX - drag.startX) / rect.width) * 100;
      const dyPct = ((e.clientY - drag.startY) / rect.height) * 100;
      if (drag.mode === "move") {
        const nx = clamp(drag.orig.x_pct + dxPct, 0, 100 - drag.orig.w_pct);
        const ny = clamp(drag.orig.y_pct + dyPct, 0, 100 - drag.orig.h_pct);
        onUpdate({ x_pct: nx, y_pct: ny });
        return;
      }
      // Directional resize. `dir` is one of n,s,e,w,ne,nw,se,sw.
      let { x_pct, y_pct, w_pct, h_pct } = drag.orig;
      const MIN_W = 4, MIN_H = 3;
      if (drag.dir.includes("e")) {
        w_pct = clamp(drag.orig.w_pct + dxPct, MIN_W, 100 - drag.orig.x_pct);
      }
      if (drag.dir.includes("s")) {
        h_pct = clamp(drag.orig.h_pct + dyPct, MIN_H, 100 - drag.orig.y_pct);
      }
      if (drag.dir.includes("w")) {
        const newX = clamp(drag.orig.x_pct + dxPct, 0, drag.orig.x_pct + drag.orig.w_pct - MIN_W);
        w_pct = drag.orig.x_pct + drag.orig.w_pct - newX;
        x_pct = newX;
      }
      if (drag.dir.includes("n")) {
        const newY = clamp(drag.orig.y_pct + dyPct, 0, drag.orig.y_pct + drag.orig.h_pct - MIN_H);
        h_pct = drag.orig.y_pct + drag.orig.h_pct - newY;
        y_pct = newY;
      }
      onUpdate({ x_pct, y_pct, w_pct, h_pct });
    };
    const onUp = () => setDrag(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [drag, onUpdate]);

  const startDrag = (mode, dir) => (e) => {
    if (!editable || sig) return;
    e.stopPropagation();
    e.preventDefault();
    setDrag({
      mode, dir,
      startX: e.clientX,
      startY: e.clientY,
      orig: { x_pct: s.x_pct, y_pct: s.y_pct, w_pct: s.w_pct, h_pct: s.h_pct },
    });
  };

  const canSignThis = !editable && !sig && !alreadySignedByMe;

  return (
    <div
      ref={boxRef}
      data-slot-id={s.id}
      data-testid={`policy-slot-${s.id}`}
      onMouseDown={editable && !sig ? startDrag("move", null) : undefined}
      onClick={(e) => {
        e.stopPropagation();
        // Only trigger sign when we didn't just finish a drag on the body.
        if (canSignThis && !drag) onSign();
      }}
      style={{
        position: "absolute",
        left: `${s.x_pct}%`,
        top: `${s.y_pct}%`,
        width: `${s.w_pct}%`,
        height: `${s.h_pct}%`,
        background: sig ? "rgba(16,185,129,0.10)" : "rgba(37,99,235,0.10)",
        border: `1.5px dashed ${sig ? "#059669" : "#2563eb"}`,
        cursor: editable && !sig ? "move" : canSignThis ? "pointer" : "default",
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
        userSelect: "none",
      }}
      title={
        editable && !sig ? "Drag to move · corner to resize · × to delete"
          : sig ? `Signed by ${sig.user_name} at ${new Date(sig.signed_at).toLocaleString()} (IP ${sig.ip})`
          : `Click to sign as ${s.label}`
      }
    >
      {sig ? (
        <div className="text-center w-full">
          <img src={sig.signature_data} alt="signature" className="max-h-[70%] mx-auto object-contain" />
          <div className="text-[8px] text-zinc-700 leading-tight px-1">
            <b>{sig.user_name}</b><br />
            {new Date(sig.signed_at).toLocaleString()}<br />
            IP {sig.ip || "—"}
          </div>
        </div>
      ) : (
        <div className="text-[10px] font-semibold uppercase tracking-wider text-blue-700 text-center px-1 pointer-events-none">
          {s.label}
        </div>
      )}

      {editable && !sig && (
        <>
          {/* Toolbar (top-right, above the box) */}
          <div
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute", top: -14, right: 0,
              display: "flex", gap: 4,
              background: "white", border: "1px solid #cbd5e1",
              borderRadius: 4, padding: "2px 3px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.12)", zIndex: 3,
            }}
          >
            <IconBtn title="Duplicate this slot" testId={`policy-slot-duplicate-${s.id}`} onClick={onDuplicate} color="#2563eb">⧉</IconBtn>
            <IconBtn title="Remove this slot" testId={`policy-slot-delete-${s.id}`} onClick={onDelete} color="#dc2626">×</IconBtn>
          </div>

          {/* 8 invisible resize handles along the border of the box. Cursor + drag do the work. */}
          {RESIZE_DIRS.map(({ dir, style, cursor }) => (
            <div
              key={dir}
              onMouseDown={startDrag("resize", dir)}
              data-testid={`policy-slot-resize-${dir}-${s.id}`}
              style={{ position: "absolute", background: "transparent", cursor, zIndex: 2, ...style }}
            />
          ))}
        </>
      )}
    </div>
  );
}

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

// Invisible strips along the box edges/corners. `t` = thickness in pixels for the hit area.
const T = 8;
const RESIZE_DIRS = [
  { dir: "n",  cursor: "ns-resize",   style: { top: -T / 2,  left: T,   right: T,  height: T } },
  { dir: "s",  cursor: "ns-resize",   style: { bottom: -T / 2, left: T, right: T,  height: T } },
  { dir: "w",  cursor: "ew-resize",   style: { top: T,   left: -T / 2, bottom: T,  width: T } },
  { dir: "e",  cursor: "ew-resize",   style: { top: T,   right: -T / 2, bottom: T, width: T } },
  { dir: "nw", cursor: "nwse-resize", style: { top: -T / 2, left: -T / 2, width: T, height: T } },
  { dir: "ne", cursor: "nesw-resize", style: { top: -T / 2, right: -T / 2, width: T, height: T } },
  { dir: "sw", cursor: "nesw-resize", style: { bottom: -T / 2, left: -T / 2, width: T, height: T } },
  { dir: "se", cursor: "nwse-resize", style: { bottom: -T / 2, right: -T / 2, width: T, height: T } },
];

function IconBtn({ children, title, testId, onClick, onMouseDown, color, cursor }) {
  return (
    <button
      type="button"
      title={title}
      data-testid={testId}
      onMouseDown={onMouseDown}
      onClick={onClick ? (e) => { e.stopPropagation(); onClick(); } : undefined}
      style={{
        width: 22,
        height: 22,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "transparent",
        border: "none",
        color: color || "#334155",
        fontSize: 14,
        lineHeight: 1,
        fontWeight: 600,
        padding: 0,
        cursor: cursor || "pointer",
        borderRadius: 3,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      {children}
    </button>
  );
}

